import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { yupResolver } from '@hookform/resolvers/yup';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InferType } from 'yup';
import { useUploadProfilePhoto } from '../../../../hooks/user/useUploadProfilePhoto';
import { personalInfoSchema } from '../../../../validation/clientProfile.schemas';

type PersonalInfoFormValues = InferType<typeof personalInfoSchema>;

export interface PersonalInfoData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  postalCode: string;
  photoUrl?: string;
  memberSince: Date;
  verified: boolean;
}

interface PersonalInfoSectionProps {
  data: PersonalInfoData;
  onSave: (data: Partial<PersonalInfoData>) => void;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({ data, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(data.photoUrl || null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadPhoto, isPending: isUploading } = useUploadProfilePhoto();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonalInfoFormValues>({
    resolver: yupResolver(personalInfoSchema),
    defaultValues: {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      photoUrl: data.photoUrl ?? null,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode,
    },
  });

  const handleEdit = () => {
    reset({
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      photoUrl: data.photoUrl ?? null,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset();
    setPhotoPreview(data.photoUrl || null);
    setIsEditing(false);
  };

  const onSubmit = (values: PersonalInfoFormValues) => {
    onSave({
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone ?? '',
      photoUrl: photoPreview ?? undefined,
      city: values.city ?? '',
      country: values.country ?? '',
      postalCode: values.postalCode ?? '',
    });
    setIsEditing(false);
    setSnackbarOpen(true);
  };

  const handlePhotoClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    // Upload to Supabase
    uploadPhoto(file);
  };

  const formatMemberSince = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getInitials = () => `${data.firstName.charAt(0)}${data.lastName.charAt(0)}`.toUpperCase();

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Personal Information
        </Typography>
        {!isEditing && (
          <IconButton onClick={handleEdit} size="small" sx={{ color: '#178B8E' }}>
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {/* Avatar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <Box
          sx={{ position: 'relative', cursor: isEditing ? 'pointer' : 'default' }}
          onClick={handlePhotoClick}
        >
          <Avatar
            src={photoPreview || undefined}
            sx={{
              width: 150,
              height: 150,
              backgroundColor: '#178B8E',
              fontSize: '3rem',
              opacity: isUploading ? 0.7 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {!photoPreview && getInitials()}
          </Avatar>
          {isEditing && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                backgroundColor: '#178B8E',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
              }}
            >
              <CameraAltIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
          )}
        </Box>

        {isUploading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Uploading...
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handlePhotoChange}
        />
        {data.verified && (
          <Chip
            icon={<CheckCircleIcon sx={{ color: '#178B8E !important' }} />}
            label="Verified"
            size="small"
            variant="outlined"
            sx={{ mt: 1.5, color: '#178B8E', borderColor: '#178B8E' }}
          />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Member since {formatMemberSince(data.memberSince)}
        </Typography>
      </Box>

      {/* Fields */}
      {isEditing ? (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Email" value={data.email} fullWidth size="small" disabled />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Phone Number"
                    fullWidth
                    size="small"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="City"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Country"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Postal Code"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={handleCancel} variant="outlined" sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', backgroundColor: '#178B8E' }}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">
              {data.firstName} {data.lastName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EmailIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">{data.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">{data.phone}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocationOnIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">
              {data.city}, {data.country} {data.postalCode}
            </Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          Personal information updated!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PersonalInfoSection;
